import { useRef } from 'react';
import { HOSTS } from '../../content/site';
import { useMotionEnabled } from '../../lib/motion';
import { useArtReady } from '../../lib/useArtReady';
import { useInViewActive } from '../../lib/useInViewActive';
import { DiamondTravel } from './DiamondTravel';

/**
 * "Meet Your Hosts" — the designer's complete triangle art (host photos,
 * outlined name labels, Awakening center, grid glow) from the LITE re-export
 * (2026-07-23, node 4395:955; re-exported 2026-07-31 replacing Extra Emily's
 * slot with the COMING SOON graphic, then 2026-08-12 for HX-41 with Emiru in
 * that slot — full lineup): host photos are pre-cut triangular alpha PNGs
 * placed as plain rects (no giant masked images), ~1.1MB vs the 6MB original,
 * so the art no longer stalls the scroll-in animation. As with the original
 * export, only the two static diamond ornaments were stripped — DiamondTravel
 * replaces them with one spark patrolling each edge of the triangle
 * (2026-07-16 direction: sparks stay on their leg, never rounding corners;
 * every leg runs the same profile in phase, so the motion is rotationally
 * symmetric).
 *
 * RE-EXPORT CHECKLIST — a raw export is never drop-in; it needs BOTH dev-side
 * edits below, then `npm run optimize-images` (the public copy is GENERATED —
 * never edit it, and never copy an export straight into public/):
 *
 *  1. HEADER REWRITE. Every lite export lands with 37px of extra side padding
 *     — the SAME artwork geometry translated +37px in x (y identical),
 *     verified byte-identical on the frame path each time. Rewrite the header
 *     to `width="1378" height="1416" viewBox="37 0 1378 1416"`, restoring the
 *     original export's exact framing (full baked drop shadow, original art
 *     scale) and leaving this component's aspect ratio, guide path, and legs
 *     untouched. Canvas height has varied between exports (7.23 shipped
 *     1452×1326 with the bottom 90px of shadow fade cropped, 8.12 the full
 *     1452×1416); the viewBox is authoritative either way — an over-tall
 *     viewBox just leaves the cropped fade blank.
 *  2. STRIP THE 2 STATIC DIAMONDS. The designer bakes two diamond ornaments
 *     onto the edges; leave them in and they render UNDERNEATH the patrolling
 *     sparks, so the triangle shows 5 diamonds instead of 3 (regression caught
 *     in QA 2026-08-12). Each is a `<g filter="url(#filterN_d_…)">` wrapping
 *     one `<path>`, PLUS its own glow `<filter>` def in `<defs>` — remove all
 *     four nodes. Filter ids are export-specific (8.12 used filter2/filter3);
 *     the reliable check is that only the frame's own filters survive, and a
 *     short-line diff against the previously committed SVG should show
 *     nothing but the swapped photo's rect + `<use>` transform.
 *
 * The guide is the verified centerline geometry in the ORIGINAL export's
 * coordinate space (grid art at 126.73, 129.36; 60° edges; corner fillets
 * from the tangent geometry) — the viewBox crop maps the lite art onto it
 * exactly. Host names are outlined paths in the artwork — invisible to
 * screen readers — so the img alt names them.
 */

const GUIDE =
  'M 171.57 132.77 H 1212.67 A 41.3 41.3 0 0 1 1248.47 194.68 L 729.07 1094.33 ' +
  'A 42.7 42.7 0 0 1 655.17 1094.33 L 135.77 194.68 A 41.3 41.3 0 0 1 171.57 132.77 Z';

// Edge legs as lap fractions of GUIDE (corner-tangent to corner-tangent).
// Re-derive if the guide changes: getTotalLength() on incremental prefixes
// of GUIDE, each boundary ÷ total (3380.98). Rest defaults to each leg's
// center = the design's static ornament spots.
const LEGS = [
  { start: 0, end: 0.30793 }, // top edge (travels left→right)
  { start: 0.3335, end: 0.64076 }, // right edge (travels down)
  { start: 0.66717, end: 0.97443 }, // left edge (travels up)
];

/** Exported for HomePage's ready-gated Reveal. */
export const HOSTS_TRIANGLE_ART = '/assets/images/frames/hosts-full-lite.svg';

export function HostsTriangle() {
  const ref = useRef<HTMLDivElement>(null);
  const motionOn = useMotionEnabled();
  // Sparks hold until the triangle art is actually painted — patrolling an
  // empty space on a cold visit reads as a glitch, not choreography.
  const artReady = useArtReady([HOSTS_TRIANGLE_ART]);
  const active = useInViewActive(ref) && motionOn && artReady;

  return (
    <div
      ref={ref}
      className="relative w-full [container-type:inline-size]"
      style={{ aspectRatio: '1378 / 1416' }}
    >
      <img
        src={HOSTS_TRIANGLE_ART}
        alt={`Your hosts: ${HOSTS.join(', ')}`}
        decoding="async"
        fetchpriority="low"
        className="absolute inset-0 h-full w-full"
      />
      <DiamondTravel
        viewBox="0 0 1378 1416"
        guidePath={GUIDE}
        legs={LEGS}
        durationS={16}
        size={87.5}
        active={active}
      />
    </div>
  );
}
